import { FormControl, Grid, IconButton, InputLabel, Select, TextField, Typography, Box, Button, Tooltip
    , Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Snackbar } from '@mui/material';
import { Add as IconAdd, Delete as IconDelete, Edit as IconEdit, Block as IconBlock } from "@mui/icons-material";
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import comprobantePagoService from "../services/comprobantePagoService";
import * as catalogoService from "../services/catalogoService";
import api from "../services/api";
import MontoTextField, { formatoMonto } from "./common/MontoTextField";
import { init, setEntity, setData, setValidationMessage, activeValidations
    , setListMedioPago, setListEntidadPago, nuevoPago } from "../redux/receiptPaymentSlice";
import { FormHelperText as HelperText } from '@mui/material';
import DialogYesNo from './DialogYesNo';
import { PaperComponentDraggable } from "./PaperComponentDraggable";
import AutocompleteRegistro from './AutocompleteRegistro';

// Detalle de pagos del comprobante. Los pagos son un segundo detalle: pueden
// registrarse junto al comprobante o despues, como pagos parciales.

// Catalogo 1 = Estados del pago del comprobante.
const ESTADO_PAGO = {
    2: "Activo",
    3: "Inactivo",
    4: "Anulado",
    227: "Pago Consolidado",
}

const ReceiptPayment = (props) => {
    const isReadOnly = props.isReadOnly;
    const dispatch = useDispatch();
    const sliceKey = props.sliceKey;
    const params = useParams();
    const setPayments = props.setPayments;
    const esNuevo = props.esNuevo;
    const permitirAgregar = props.permitirAgregar !== undefined ? props.permitirAgregar : !isReadOnly;
    // Los pagos se traen del comprobante: el identificador llega por la ruta
    // (pagina) o por prop (dialogo).
    const idComprobante = props.idComprobante != null ? props.idComprobante : params.id;

    useEffect(() => {
        dispatch(init({ sliceKey }));
    }, [dispatch, sliceKey]);

    const entity = useSelector((state) => state.receiptPayment[sliceKey]?.entity);
    const listMedioPago = useSelector((state) => state.receiptPayment[sliceKey]?.listMedioPago);
    const listEntidadPago = useSelector((state) => state.receiptPayment[sliceKey]?.listEntidadPago);
    const validate = useSelector((state) => state.receiptPayment[sliceKey]?.validationActive);
    // El pago hereda la moneda y el tipo de cambio del comprobante, para no repetirlos.
    const comprobante = useSelector((state) => state.receipt["Normal"]?.entity);

    const [listPayments, setListPayments] = useState([]);
    const [editingIndex, setEditingIndex] = useState(-1);
    const [openDialogDelete, setOpenDialogDelete] = useState(false)
    const openCloseDialogDelete = () => { setOpenDialogDelete(!openDialogDelete); }
    const [selectIndexDelete, setSelectIndexDelete] = useState(-1);

    const accesos = useSelector((state) => state.seguridad?.accesos) || [];
    const seguridadActiva = useSelector((state) => state.seguridad?.seguridadActiva) || "N";
    const puedeAnularPago = !isReadOnly && (seguridadActiva !== "S" || accesos.includes("pago.anular"));

    const [listMotivosAnulacion, setListMotivosAnulacion] = useState([]);
    const [openDialogAnular, setOpenDialogAnular] = useState(false);
    const [pagoAAnular, setPagoAAnular] = useState(null);
    const [idMotivoAnulacion, setIdMotivoAnulacion] = useState(0);
    const [anulando, setAnulando] = useState(false);
    const [anulacionError, setAnulacionError] = useState("");
    const [snackbarMsg, setSnackbarMsg] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const redondear = (val) => Math.round(((Number(val) || 0) + Number.EPSILON) * 100) / 100;
    const montoTotalComprobante = redondear(comprobante?.montoTotal || 0);

    const sumaOtrosPagos = (listPayments || []).reduce((acc, p, idx) => {
        if (idx === editingIndex) return acc;
        if (Number(p.idEstado) === 4 || Number(p.idEstado) === 146 || p.estado === "Anulado") return acc;
        return acc + (parseFloat(p.monto) || 0);
    }, 0);

    const saldoMaximo = Math.max(0, redondear(montoTotalComprobante - sumaOtrosPagos));

    const validation = {
        idMedioPago: {
            error: entity?.idMedioPago == 0 || entity?.idMedioPago == null,
            message: "Seleccione el medio de pago",
        },
        fechaPago: {
            error: !entity?.fechaPago || (comprobante?.fecha && moment(entity?.fechaPago).isBefore(moment(comprobante.fecha), 'day')),
            message: !entity?.fechaPago
                ? "Ingrese la fecha del pago"
                : `La fecha de pago no puede ser anterior a la emisión (${moment(comprobante.fecha).format('DD/MM/YYYY')})`,
        },
        monto: {
            error: (() => {
                const m = parseFloat(entity?.monto);
                if (entity?.monto == null || entity?.monto === "" || isNaN(m) || m <= 0) return true;
                if (montoTotalComprobante > 0 && m > redondear(saldoMaximo + 0.0001)) return true;
                if (montoTotalComprobante <= 0 && esNuevo) return true;
                return false;
            })(),
            message: (() => {
                const m = parseFloat(entity?.monto);
                if (entity?.monto == null || entity?.monto === "" || isNaN(m) || m <= 0) return "Ingrese el monto del pago";
                if (montoTotalComprobante <= 0 && esNuevo) return "Ingrese primero el detalle del comprobante para definir el total";
                if (montoTotalComprobante > 0 && m > redondear(saldoMaximo + 0.0001)) {
                    return `El monto no puede superar el saldo pendiente (${formatoMonto(saldoMaximo)})`;
                }
                return "";
            })(),
        },
    }

    const messagesValidation = () => {
        var messages = [];

        Object.keys(validation).forEach(function (key) {
            if (validation[key].error) messages.push(validation[key].message)
        })

        return messages;
    }

    const fetchListMedioPago = useCallback(async () => {
        const data = await catalogoService.getCatalogosByPadre(200)
            .catch((err) => { console.log("Err: ", err) });
        dispatch(setListMedioPago({sliceKey: sliceKey, data: data || []}));
    }, [dispatch, sliceKey]);
    const fetchListEntidadPago = useCallback(async () => {
        // Catalogo 210 = Entidad de Pago
        const data = await catalogoService.getCatalogosByPadre(210)
            .catch((err) => { console.log("Err: ", err) });
        dispatch(setListEntidadPago({sliceKey: sliceKey, data: data || []}));
    }, [dispatch, sliceKey]);
    const fetchListMotivos = async () => {
        const data = await catalogoService.getCatalogosByPadre(220)
            .catch((err) => { console.log("Err: ", err) });
        setListMotivosAnulacion(data || []);
    }
    const fetchPayments = useCallback(async () => {
        if (idComprobante != null && parseInt(idComprobante) > 0) {
            const pagos = await comprobantePagoService.getsComprobantePagoView(idComprobante)
                .catch((err) => { console.log("Err: ", err) });

            setListPayments(pagos ?? []);
            if (setPayments) setPayments(pagos ?? []);
        }
    }, [idComprobante]);

    useEffect(() => {
        fetchListMedioPago();
        fetchListEntidadPago();
        fetchListMotivos();
        fetchPayments();
    }, [fetchPayments, fetchListEntidadPago, fetchListMedioPago]);

    useEffect(() => {
        dispatch(setValidationMessage({sliceKey: sliceKey, message: messagesValidation()}));
    }, [entity, listPayments, editingIndex, comprobante?.montoTotal, comprobante?.fecha])

    // Los pagos que ya estan en la base llegan con IdComprobante; los que se
    // agregan en esta pantalla todavia no lo tienen. Los pagos ya registrados no
    // se editan ni se borran desde aqui, por eso en sus acciones se muestra el
    // estado del pago.
    const esPagoRegistrado = (row) => (row?.idComprobante || 0) > 0 || (row?.id || 0) > 0;

    // Cuando los pagos no son editables (comprobante ya guardado) la columna de
    // acciones pasa a mostrar solo el estado de cada pago.
    const esEditablePagos = () => !isReadOnly;

    const estadoPagoDescripcion = (row) => row?.estado || ESTADO_PAGO[row?.idEstado] || "";

    const handleAddPayment = () => {
        var messages = messagesValidation();
        if (messages.length > 0) {
            dispatch(activeValidations({sliceKey: sliceKey}));
            return;
        }

        var newPayment = {
            id: 0,
            numero: listPayments.length + 1,
            idMedioPago: entity.idMedioPago,
            medioPago: entity.medioPago,
            idEntidadPago: entity.idEntidadPago,
            entidadPago: entity.entidadPago,
            numeroCuenta: entity.numeroCuenta,
            numeroOperacion: entity.numeroOperacion,
            numeroVoucher: entity.numeroVoucher,
            fechaPago: entity.fechaPago,
            // Hereda la moneda y el tipo de cambio del comprobante.
            idMoneda: comprobante?.idMoneda || 0,
            moneda: comprobante?.moneda || "",
            tipoCambio: comprobante?.tipoCambio || 1,
            monto: parseFloat(entity.monto) || 0,
            observaciones: entity.observaciones,
            idEstado: 2,
        };

        var newList = [...listPayments, newPayment];
        setListPayments(newList);
        if (setPayments) setPayments(newList);
        dispatch(setEntity({sliceKey: sliceKey, data: nuevoPago()}));
    }

    const handleEditPayment = (index) => {
        if (esPagoRegistrado(listPayments[index])) return;

        setEditingIndex(index);
        dispatch(setEntity({sliceKey: sliceKey, data: listPayments[index]}));
    }

    const handleUpdatePayment = () => {
        var messages = messagesValidation();
        if (messages.length > 0) {
            dispatch(activeValidations({sliceKey: sliceKey}));
            return;
        }

        var updatedPayment = {
            ...listPayments[editingIndex],
            idMedioPago: entity.idMedioPago,
            medioPago: entity.medioPago,
            idEntidadPago: entity.idEntidadPago,
            entidadPago: entity.entidadPago,
            numeroCuenta: entity.numeroCuenta,
            numeroOperacion: entity.numeroOperacion,
            numeroVoucher: entity.numeroVoucher,
            fechaPago: entity.fechaPago,
            idMoneda: comprobante?.idMoneda || listPayments[editingIndex].idMoneda,
            moneda: comprobante?.moneda || listPayments[editingIndex].moneda,
            tipoCambio: comprobante?.tipoCambio || listPayments[editingIndex].tipoCambio,
            monto: parseFloat(entity.monto) || 0,
            observaciones: entity.observaciones,
        };

        var newList = [...listPayments];
        newList[editingIndex] = updatedPayment;
        setListPayments(newList);
        if (setPayments) setPayments(newList);
        setEditingIndex(-1);
        dispatch(setEntity({sliceKey: sliceKey, data: nuevoPago()}));
    }

    const eliminarPayment = (index) => {
        var newList = listPayments.filter((_, i) => i !== index);
        setListPayments(newList);
        if (setPayments) setPayments(newList);

        if (editingIndex === index) {
            setEditingIndex(-1);
            dispatch(setEntity({sliceKey: sliceKey, data: nuevoPago()}));
        }
    }

    const handleDeletePayment = (index) => {
        if (esPagoRegistrado(listPayments[index])) return;

        if (esNuevo) {
            eliminarPayment(index);
            return;
        }

        setSelectIndexDelete(index);
        openCloseDialogDelete();
    }

    const handleCancelEdit = () => {
        setEditingIndex(-1);
        dispatch(setEntity({sliceKey: sliceKey, data: nuevoPago()}));
    }

    const handleOpenAnular = (row) => {
        setPagoAAnular(row);
        setIdMotivoAnulacion(0);
        setAnulacionError("");
        setOpenDialogAnular(true);
    };

    const handleConfirmAnularPago = async () => {
        if (!pagoAAnular || !idMotivoAnulacion || idMotivoAnulacion <= 0) {
            setAnulacionError("Seleccione el motivo de anulación.");
            return;
        }

        setAnulando(true);
        setAnulacionError("");
        try {
            await comprobantePagoService.anularComprobantePago(
                idComprobante,
                pagoAAnular.numero,
                idMotivoAnulacion
            );
            setAnulando(false);
            setOpenDialogAnular(false);
            setSnackbarMsg(`Se anuló el pago N° ${pagoAAnular.numero} (${pagoAAnular.medioPago} - ${formatoMonto(pagoAAnular.monto)}) con éxito.`);
            setSnackbarOpen(true);
            setPagoAAnular(null);
            setIdMotivoAnulacion(0);

            await fetchPayments();

            if (props.onPaymentAnnulled) {
                props.onPaymentAnnulled();
            }
        } catch (err) {
            setAnulando(false);
            const msg = err?.response?.data?.detail || err?.response?.data?.Detail || err?.response?.data?.message || "No se pudo anular el pago.";
            setAnulacionError(msg);
        }
    };

    const setDataEntity = e => {
        dispatch(setData({sliceKey: sliceKey, name: e.target.name, value: e.target.value}))
    }

    return (
        <Grid container>
            <Grid item xs={12}>
                <Box sx={{ fontWeight: 'bold' }} padding={0}>
                    <Typography variant="h7">
                        Pagos del Comprobante
                    </Typography>
                </Box>
            </Grid>
            {/* Los campos para agregar un pago solo aparecen cuando se puede
                agregar. El comprobante ya guardado no admite pagos desde aqui. */}
            {!isReadOnly && permitirAgregar && (
            <Grid container spacing={1} padding={"4px 0px 0px 0px"} alignItems="flex-start">
                <Grid item xs={6} sm={2.5}>
                    <FormControl fullWidth>
                        <AutocompleteRegistro
                            name="idMedioPago"
                            label="Medio de Pago"
                            required
                            texto={entity?.medioPago || ""}
                            idSeleccionado={entity?.idMedioPago || 0}
                            opciones={listMedioPago || []}
                            tipoRegistro="medio de pago"
                            disabled={isReadOnly}
                            size="small"
                            error={Boolean(validate?.idMedioPago && validation.idMedioPago.error)}
                            helperText={validate?.idMedioPago && validation.idMedioPago.error ? validation.idMedioPago.message : ""}
                            onTexto={(newValue) => {
                                dispatch(setData({ sliceKey: sliceKey, name: "medioPago", value: newValue || "" }));
                            }}
                            onSeleccionar={(newValue) => {
                                dispatch(setData({ sliceKey: sliceKey, name: "idMedioPago", value: newValue?.id || 0 }));
                                dispatch(setData({ sliceKey: sliceKey, name: "medioPago", value: newValue?.descripcion || "" }));
                            }}
                            onBuscar={async (texto) => {
                                return await catalogoService.getCatalogosByPadre(200, texto);
                            }}
                            onRegistrarNuevo={async (texto) => {
                                const nuevoId = await catalogoService.addUpdateCatalogo({
                                    idCatalogo: 0,
                                    idPadre: 200,
                                    descripcion: texto,
                                    codigo: "",
                                    estado: "A"
                                });
                                if (nuevoId > 0) {
                                    await fetchListMedioPago();
                                    return { id: nuevoId, descripcion: texto };
                                }
                                return null;
                            }}
                            puedeRegistrar={!isReadOnly}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={6} sm={2}>
                    <FormControl fullWidth>
                        <AutocompleteRegistro
                            name="idEntidadPago"
                            label="Entidad"
                            texto={entity?.entidadPago || ""}
                            idSeleccionado={entity?.idEntidadPago || 0}
                            opciones={listEntidadPago || []}
                            tipoRegistro="entidad de pago"
                            disabled={isReadOnly}
                            size="small"
                            onTexto={(newValue) => {
                                dispatch(setData({ sliceKey: sliceKey, name: "entidadPago", value: newValue || "" }));
                            }}
                            onSeleccionar={(newValue) => {
                                dispatch(setData({ sliceKey: sliceKey, name: "idEntidadPago", value: newValue?.id || 0 }));
                                dispatch(setData({ sliceKey: sliceKey, name: "entidadPago", value: newValue?.descripcion || "" }));
                            }}
                            onBuscar={async (texto) => {
                                return await catalogoService.getCatalogosByPadre(210, texto);
                            }}
                            onRegistrarNuevo={async (texto) => {
                                const nuevoId = await catalogoService.addUpdateCatalogo({
                                    idCatalogo: 0,
                                    idPadre: 210,
                                    descripcion: texto,
                                    codigo: "",
                                    estado: "A"
                                });
                                if (nuevoId > 0) {
                                    await fetchListEntidadPago();
                                    return { id: nuevoId, descripcion: texto };
                                }
                                return null;
                            }}
                            puedeRegistrar={!isReadOnly}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={6} sm={1.5}>
                    <TextField
                        name="numeroOperacion"
                        label="N. Operacion"
                        fullWidth
                        value={entity?.numeroOperacion || ""}
                        onChange={setDataEntity}
                        disabled={isReadOnly}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={6} sm={1.5}>
                    <TextField
                        name="numeroVoucher"
                        label="N. Voucher"
                        fullWidth
                        value={entity?.numeroVoucher || ""}
                        onChange={setDataEntity}
                        disabled={isReadOnly}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={6} sm={1.8}>
                    <TextField
                        required
                        type="date"
                        name="fechaPago"
                        label="Fecha de Pago"
                        fullWidth
                        value={moment(entity?.fechaPago).format('yyyy-MM-DD')}
                        onChange={setDataEntity}
                        error={validate?.fechaPago && validation.fechaPago.error}
                        helperText={validate?.fechaPago && validation.fechaPago.error ? validation.fechaPago.message : ""}
                        disabled={isReadOnly}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={6} sm={1.7}>
                    <MontoTextField
                        name="monto"
                        label="Monto"
                        fullWidth
                        value={entity?.monto ?? ""}
                        onChange={setDataEntity}
                        error={validate?.monto && validation.monto.error}
                        helperText={validate?.monto && validation.monto.error ? validation.monto.message : ""}
                        disabled={isReadOnly}
                        size="small"
                    />
                </Grid>
                <Grid item xs={6} sm={1}>
                    <Box display="flex" alignItems="center" gap={1} sx={{ pt: "2px" }}>
                        {editingIndex >= 0 ? (
                            <>
                                <Button variant="contained" size="small" onClick={handleUpdatePayment} sx={{ minWidth: 60 }}>Guardar</Button>
                                <Button variant="outlined" size="small" onClick={handleCancelEdit} sx={{ minWidth: 40 }}>X</Button>
                            </>
                        ) : (
                            <Tooltip title="Agregar">
                                <Button variant="contained" size="small" onClick={handleAddPayment} aria-label="Agregar" sx={{ minWidth: 38, height: 38 }}>
                                    <IconAdd />
                                </Button>
                            </Tooltip>
                        )}
                    </Box>
                </Grid>
            </Grid>
            )}
            {/* Alerta informativa si el comprobante ya está 100% cancelado */}
            {!isReadOnly && !esNuevo && (parseFloat(comprobante?.montoSaldo) || 0) <= 0 && listPayments.length > 0 && (
                <Grid item xs={12} sx={{ pt: 1, pb: 1 }}>
                    <Alert severity="info" sx={{ py: 0.5 }}>
                        El comprobante se encuentra cancelado en su totalidad (Saldo: 0.00). Si necesita corregir un pago específico (ej. transferencia rechazada o error de digitación), puede anularlo en la grilla para restablecer el saldo y registrar el abono correcto.
                    </Alert>
                </Grid>
            )}

            {/* La tabla de pagos siempre se muestra. */}
            <Grid item xs={12} sx={{ mt: isReadOnly ? 0.5 : 1 }}>
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>N°</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Medio de Pago</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Entidad</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>N. Operacion</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Monto</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 100 }}>Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {listPayments.map((row, index) => {
                                const esRegistrado = esPagoRegistrado(row);
                                const esActivo = Number(row.idEstado) === 2;
                                const esAnulado = Number(row.idEstado) === 4;
                                const esConsolidado = Number(row.idEstado) === 227;

                                return (
                                    <TableRow key={index} sx={esAnulado ? { backgroundColor: 'action.hover', opacity: 0.7 } : {}}>
                                        <TableCell align="center">{row.numero || index + 1}{row.idComprobantePago ? <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.68rem', fontFamily: 'monospace' }}>ID:{row.idComprobantePago}</Typography> : null}</TableCell>
                                        <TableCell>{row.medioPago}</TableCell>
                                        <TableCell>{row.entidadPago}</TableCell>
                                        <TableCell>{row.numeroOperacion}</TableCell>
                                        <TableCell align="center">{moment(row.fechaPago).format('DD/MM/YYYY')}</TableCell>
                                        <TableCell align="right" sx={esAnulado ? { textDecoration: 'line-through' } : {}}>{formatoMonto(row.monto)}</TableCell>
                                        <TableCell align="center">
                                            {esActivo && (
                                                <Typography variant="body2" sx={{ color: "success.main", fontWeight: 600 }}>Activo</Typography>
                                            )}
                                            {esAnulado && (
                                                <Tooltip title={row.motivoAnulacion ? `Motivo: ${row.motivoAnulacion}` : "Pago Anulado"}>
                                                    <Typography variant="body2" sx={{ color: "error.main", fontWeight: 600 }}>Anulado</Typography>
                                                </Tooltip>
                                            )}
                                            {esConsolidado && (
                                                <Typography variant="body2" sx={{ color: "info.main", fontWeight: 600 }}>Consolidado</Typography>
                                            )}
                                            {!esActivo && !esAnulado && !esConsolidado && (
                                                <Typography variant="body2">{estadoPagoDescripcion(row)}</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            {esRegistrado ? (
                                                esActivo ? (
                                                    puedeAnularPago ? (
                                                        <Tooltip title="Anular este pago">
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="error"
                                                                startIcon={<IconBlock fontSize="small" />}
                                                                onClick={() => handleOpenAnular(row)}
                                                                sx={{ textTransform: "none", py: 0.2, px: 1, fontSize: "0.75rem", minHeight: 24 }}
                                                            >
                                                                Anular
                                                            </Button>
                                                        </Tooltip>
                                                    ) : "-"
                                                ) : esConsolidado ? (
                                                    <Tooltip title="Pago consolidado: requiere desconsolidar antes de anular">
                                                        <span>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                disabled
                                                                startIcon={<IconBlock fontSize="small" />}
                                                                sx={{ textTransform: "none", py: 0.2, px: 1, fontSize: "0.75rem", minHeight: 24 }}
                                                            >
                                                                Anular
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                ) : "-"
                                            ) : (
                                                <>
                                                    <IconButton size="small" onClick={() => handleEditPayment(index)} title="Editar">
                                                        <IconEdit fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={() => handleDeletePayment(index)} title="Eliminar">
                                                        <IconDelete fontSize="small" />
                                                    </IconButton>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {listPayments.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography variant="body2" color="textSecondary">
                                            No hay pagos registrados
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
            <DialogYesNo
                open={openDialogDelete}
                openClose={openCloseDialogDelete}
                setYes={() => eliminarPayment(selectIndexDelete)}
                setNo={() => { return }}
                titulo={"Borrar Pago"}
                texto={"Esta seguro de borrar el pago del comprobante?"}
            ></DialogYesNo>

            {/* Dialogo interactivo para anular un pago especifico */}
            <Dialog open={openDialogAnular} onClose={() => !anulando && setOpenDialogAnular(false)} fullWidth maxWidth="xs" PaperComponent={PaperComponentDraggable}>
                <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
                    <Typography variant="h6" component="span" sx={{ fontWeight: "bold" }}>
                        Anular Pago
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {anulacionError && (
                        <Alert severity="error" sx={{ mb: 1, mt: 1 }}>
                            {anulacionError}
                        </Alert>
                    )}
                    <Grid container spacing={1} sx={{ pt: 1 }}>
                        <Grid item xs={6}>
                            <TextField
                                disabled
                                label="Pago N°"
                                fullWidth
                                value={pagoAAnular ? pagoAAnular.numero : ""}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                disabled
                                label="Monto"
                                fullWidth
                                value={pagoAAnular ? formatoMonto(pagoAAnular.monto) : ""}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                disabled
                                label="Medio de Pago"
                                fullWidth
                                value={pagoAAnular ? (pagoAAnular.medioPago + (pagoAAnular.numeroOperacion ? ` (Op: ${pagoAAnular.numeroOperacion})` : "")) : ""}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth required error={Boolean(anulacionError && (!idMotivoAnulacion || idMotivoAnulacion <= 0))}>
                                <InputLabel htmlFor="select-motivo-anulacion" shrink>Motivo de Anulación</InputLabel>
                                <Select
                                    native
                                    label="Motivo de Anulación"
                                    value={idMotivoAnulacion}
                                    onChange={(e) => {
                                        setIdMotivoAnulacion(parseInt(e.target.value));
                                        setAnulacionError("");
                                    }}
                                    inputProps={{ id: 'select-motivo-anulacion' }}
                                    size="small"
                                >
                                    <option value={0}>{"(Seleccione Motivo)"}</option>
                                    {listMotivosAnulacion.map((row) => (
                                        <option key={row.id} value={row.id}>{row.descripcion}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialogAnular(false)} disabled={anulando}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmAnularPago}
                        disabled={anulando || !idMotivoAnulacion || idMotivoAnulacion <= 0}
                    >
                        {anulando ? "Anulando..." : "Anular Pago"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
                    {snackbarMsg}
                </Alert>
            </Snackbar>
        </Grid>
    )
}
export default ReceiptPayment;
